import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly baseUrl = 'https://cloud.capawesome.io';

  constructor(
    private readonly meta: Meta,
    private readonly title: Title,
  ) {}

  public setDescription(description: string): void {
    this.meta.updateTag({
      name: 'description',
      content: description,
    });
    this.meta.updateTag({
      property: 'og:description',
      content: description,
    });
    this.meta.updateTag({
      name: 'twitter:description',
      content: description,
    });
  }

  public setKeywords(keywords: string[]): void {
    this.meta.updateTag({
      name: 'keywords',
      content: keywords.join(', '),
    });
  }

  public setTitle(title: string): void {
    const titleWithSuffix = `${title} - Capawesome Cloud`;
    this.title.setTitle(titleWithSuffix);
    this.meta.updateTag({
      property: 'og:title',
      content: titleWithSuffix,
    });
    this.meta.updateTag({
      name: 'twitter:title',
      content: titleWithSuffix,
    });
    this.updateOgImage();
    this.updateCanonical();
  }

  private updateCanonical(): void {
    const canonicalUrl = `${this.baseUrl}${this.router.url}`;
    let linkElement = this.document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;
    if (!linkElement) {
      linkElement = this.document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      // Use appendChild instead of append — Domino (SSR DOM) doesn't support append.
      // eslint-disable-next-line unicorn/prefer-dom-node-append
      this.document.head.appendChild(linkElement);
    }
    linkElement.setAttribute('href', canonicalUrl);
  }

  private updateOgImage(): void {
    // Get current URL - use router URL during SSR, browser location in browser
    let currentUrl: string;
    if (isPlatformBrowser(this.platformId)) {
      currentUrl = this.document.location.href;
    } else {
      // During SSR, construct URL from router path and base URL
      const path = this.router.url;
      currentUrl = `${this.baseUrl}${path}`;
    }

    this.meta.updateTag({
      property: 'og:url',
      content: currentUrl,
    });
  }
}
